/**
 * ESPECIALIDADES — o MECANISMO (sem dado de cliente).
 *
 * A especialidade da clínica (`ESPECIALIDADE`, em `src/shared/cliente/clinica.ts`)
 * decide QUAIS recursos de especialidade podem existir no app. Cada clínica
 * liga só o que usa (`features.ts`); o interruptor só vale se a especialidade
 * PERMITE o recurso. Assim, mesmo um `features.ts` errado não mostra Unimed
 * para dentista nem odontograma para estética.
 *
 * Client-safe: só dados puros. Não importa de `features.ts` (evita ciclo).
 */
import { ESPECIALIDADE } from "@/shared/cliente/clinica";
import type { PacoteId } from "@/shared/pacotes";

export type EspecialidadeId = "odonto" | "medica" | "estetica";

/** Recursos que só algumas especialidades têm (chaves de `FEATURES`). */
export const SPECIALTY_FEATURES = ["cirurgias", "sus", "unimed", "odontograma"] as const;
export type SpecialtyFeatureKey = (typeof SPECIALTY_FEATURES)[number];

/** Escolhas-padrão do onboarding (`pnpm novo-cliente`) que a especialidade sugere. */
export type PadroesOnboarding = {
  readonly galeriaImagens: boolean;
  readonly orcamentos: boolean;
  readonly odontograma: boolean;
};

export type Especialidade = {
  readonly label: string;
  /** Recursos de especialidade que ela permite (a clínica ainda decide ligar). */
  readonly recursos: readonly SpecialtyFeatureKey[];
  readonly pacotePadrao: PacoteId;
  /** Vocabulário (palavra, nunca comportamento). */
  readonly clinicTypePadrao: "odonto" | "medico";
  readonly padroes: PadroesOnboarding;
};

export const ESPECIALIDADES: Record<EspecialidadeId, Especialidade> = {
  odonto: {
    label: "Odontologia",
    recursos: ["odontograma"],
    pacotePadrao: "odonto",
    clinicTypePadrao: "odonto",
    padroes: { galeriaImagens: true, orcamentos: true, odontograma: true },
  },
  medica: {
    label: "Médica",
    recursos: ["cirurgias", "sus", "unimed"],
    pacotePadrao: "generico",
    clinicTypePadrao: "medico",
    padroes: { galeriaImagens: false, orcamentos: false, odontograma: false },
  },
  estetica: {
    label: "Estética",
    recursos: [],
    pacotePadrao: "generico",
    clinicTypePadrao: "medico",
    padroes: { galeriaImagens: true, orcamentos: true, odontograma: false },
  },
};

export const ESPECIALIDADE_IDS = Object.keys(ESPECIALIDADES) as EspecialidadeId[];

export function isSpecialtyFeature(key: string): key is SpecialtyFeatureKey {
  return (SPECIALTY_FEATURES as readonly string[]).includes(key);
}

let atual: EspecialidadeId = ESPECIALIDADE;

/** SÓ para testes (tests/support/mundo.ts): simula outra especialidade no arquivo de teste. */
export function definirEspecialidadeParaTeste(id: EspecialidadeId): void {
  atual = id;
}

/** A especialidade da clínica permite este recurso? Chave que não é de especialidade → sempre sim. */
export function isAllowedBySpecialty(key: string, especialidade: EspecialidadeId = atual): boolean {
  if (!isSpecialtyFeature(key)) return true;
  return ESPECIALIDADES[especialidade].recursos.includes(key);
}
